"use client";

import { Play } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import type { PanelVideo } from "./data";
import { LONDON_PANELS, NY_PANELS } from "./data";

interface VsPanelsProps {
  className?: string;
}

type PanelCity = "new-york" | "london";

const PANEL_CITIES: readonly {
  id: PanelCity;
  label: string;
  panels: readonly PanelVideo[];
}[] = [
  { id: "new-york", label: "New York", panels: NY_PANELS },
  { id: "london", label: "London", panels: LONDON_PANELS },
];

/**
 * "Panels" — a video grid of the panel recordings, sitting between Speakers
 * and Previously in the chapter nav. A city toggle flicks between New York
 * (`NY_PANELS`, the default) and London (`LONDON_PANELS`, flattened from
 * `SCHEDULE`). Cities with no recordings are left out of the toggle.
 */
export function VsPanels({ className }: VsPanelsProps) {
  const cities = PANEL_CITIES.filter((city) => city.panels.length > 0);
  const [activeId, setActiveId] = useState<PanelCity>(
    cities[0]?.id ?? "new-york"
  );
  const active = cities.find((city) => city.id === activeId) ?? cities[0];

  return (
    <div
      id="panels"
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <h2 className="mb-3 text-[40px] leading-[1.2] font-normal tracking-[-0.04em] text-white max-md:text-[32px]">
        Panels.
      </h2>
      <p className="mb-8 max-w-[640px] text-[14px] leading-[1.6] text-white/70">
        Watch the panel recordings from New York and London.
      </p>

      {cities.length > 1 ? (
        <div
          role="group"
          aria-label="Panel city"
          className="mb-8 inline-flex rounded-full bg-white/10 p-1"
        >
          {cities.map((city) => {
            const selected = city.id === active?.id;
            return (
              <button
                key={city.id}
                type="button"
                aria-pressed={selected}
                onClick={() => setActiveId(city.id)}
                className={cn(
                  "rounded-full px-5 py-2 text-[14px] transition-colors motion-reduce:transition-none",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-white",
                  selected
                    ? "bg-white text-[#0d0d0d]"
                    : "text-white/70 hover:text-white"
                )}
              >
                {city.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {active ? <PanelGrid panels={active.panels} /> : null}
    </div>
  );
}

function PanelGrid({ panels }: { panels: readonly PanelVideo[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {panels.map((panel) => (
        <PanelCard key={panel.title} panel={panel} />
      ))}
    </div>
  );
}

/** One panel card — thumbnail with a play affordance, title, speakers, and
 *  a "Watch on YouTube" link once `videoHref` is set (falls back to
 *  "Recording coming soon" otherwise). */
function PanelCard({ panel }: { panel: PanelVideo }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-[#171717]">
      <div className="relative aspect-video w-full bg-[#1c1c1c]">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={panel.thumbSrc}
          alt=""
          aria-hidden
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {panel.videoHref ? (
          <a
            href={panel.videoHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Watch the panel: ${panel.title}`}
            className={cn(
              "absolute inset-0 flex items-center justify-center bg-black/0 transition-colors motion-reduce:transition-none",
              "hover:bg-black/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset"
            )}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#0d0d0d]">
              <Play size={20} fill="currentColor" aria-hidden />
            </span>
          </a>
        ) : null}
      </div>
      <div className="p-4">
        <h3 className="text-[16px] font-medium tracking-[-0.01em] text-white">
          {panel.title}
        </h3>
        <p className="mt-1 text-[13px] text-white/55">{panel.speakers}</p>
        {panel.videoHref ? (
          <a
            href={panel.videoHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "mt-3 inline-flex items-center gap-1 text-[14px] text-[#95999d]",
              "transition-colors hover:text-[#f9f9f9] motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-1 focus-visible:ring-offset-[#171717]"
            )}
          >
            Watch on YouTube <span aria-hidden>→</span>
          </a>
        ) : (
          <span className="mt-3 inline-flex items-center gap-1 text-[14px] text-white/40">
            Recording coming soon
          </span>
        )}
      </div>
    </div>
  );
}
